import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link, router, useForm } from '@inertiajs/react'
import { AnimatePresence, motion } from 'framer-motion'
import WorkerProfileCard from '@/frontend/components/DigitalWorkers/WorkerProfileCard'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { SlideButton } from '@/components/ui/slide-button'
import { cn } from '@/lib/utils'
import { ArrowLeft, ArrowRight, Loader2 } from 'lucide-react'
import AvatarStudio, { type AvatarStudioHandle } from '@/frontend/components/DigitalWorkers/AvatarStudio'
import IntegrationsSection from '@/frontend/components/DigitalWorkers/IntegrationsSection'
import StepTransition from '@/frontend/components/DigitalWorkers/StepTransition'
import OnboardingChat from '@/frontend/components/DigitalWorkers/OnboardingChat'
import IdentityStep from '@/frontend/pages/Dashboard/DigitalWorkers/new/IdentityStep'
import ResponsibleStep from '@/frontend/pages/Dashboard/DigitalWorkers/new/ResponsibleStep'
import SubscribeStep from '@/frontend/pages/Dashboard/DigitalWorkers/new/SubscribeStep'
import { PERSONALITY_DIMENSIONS, jobTitleForFleetProfile, parsePersonalityTraits } from '@/frontend/pages/Dashboard/DigitalWorkers/constants'
import {
  isValidBrazilianWhatsApp,
  stepForField,
  stripWhatsAppDigits,
  type DigitalWorkerCreateField,
  type DigitalWorkerCreateFormData,
  type FieldErrors,
  type OnboardingWizardStepNumber,
} from '@/frontend/pages/Dashboard/DigitalWorkers/new/types'
import type {
  AvailableIntegration,
  BillingPlanOption,
  DigitalWorkerDetail,
  GoogleAccountOption,
  IntegrationToolkit,
  OrganizationContext,
  OrgIntegration,
} from '@/frontend/pages/Dashboard/DigitalWorkers/types'
import { formatWhatsApp, parseWhatsAppInput } from '@/frontend/utils/whatsappMask'
import { useWorkerBornCelebration } from '@/frontend/hooks/useWorkerBornCelebration'

interface Props {
  worker: DigitalWorkerDetail
  integrations: OrgIntegration[]
  availableIntegrations: AvailableIntegration[]
  integrationToolkits: IntegrationToolkit[]
  integrationCategories: string[]
  organizationContext?: OrganizationContext | null
  googleAdsAccounts: GoogleAccountOption[]
  googleAnalyticsProperties: GoogleAccountOption[]
  metaAdAccounts: GoogleAccountOption[]
  canManage: boolean
  currentUserName?: string | null
  currentUserPhone?: string | null
  initialModalToOpen?: 'google_accounts' | 'meta_accounts' | null
  showIdentityHero?: boolean
  billingPlans: BillingPlanOption[]
  canGrantAdminSubscription?: boolean
  onConfirmStateChange?: (active: boolean) => void
}

type OnboardingStage = 'subscription' | 'identity' | 'responsible' | 'avatar' | 'integrations' | 'chat'
const RECOMMENDED_INTEGRATION_COUNT = 2

const STAGE_BY_STEP: Record<OnboardingWizardStepNumber, OnboardingStage> = {
  2: 'subscription',
  3: 'responsible',
  4: 'identity',
  5: 'avatar',
  6: 'integrations',
  7: 'chat',
}

function stageFromSearchParams(initialModalToOpen?: 'google_accounts' | 'meta_accounts' | null): OnboardingWizardStepNumber {
  if (typeof window === 'undefined') return initialModalToOpen ? 5 : 2

  const stage = new URLSearchParams(window.location.search).get('onboarding_step')

  if (stage === 'subscription') return 2
  if (stage === 'responsible') return 3
  if (stage === 'identity') return 4
  if (stage === 'avatar') return 5
  if (stage === 'integrations') return 6
  if (stage === 'chat') return 7

  return initialModalToOpen ? 6 : 2
}

function replaceOnboardingStepInUrl(step: OnboardingWizardStepNumber) {
  if (typeof window === 'undefined') return

  const url = new URL(window.location.href)
  url.searchParams.set('onboarding_step', STAGE_BY_STEP[step])
  window.history.replaceState({}, '', url.toString())
}

function validateStep(
  step: OnboardingWizardStepNumber,
  data: DigitalWorkerCreateFormData,
  gatewayChannelsSupported: boolean,
): FieldErrors {
  const errors: FieldErrors = {}

  if (step === 3) {
    if (!data.contact_name.trim()) errors.contact_name = 'Informe o nome do responsável.'
    if (gatewayChannelsSupported) {
      if (!data.contact_whatsapp.trim()) {
        errors.contact_whatsapp = 'Informe o WhatsApp do responsável.'
      } else if (!isValidBrazilianWhatsApp(data.contact_whatsapp)) {
        errors.contact_whatsapp = 'Use um WhatsApp brasileiro com DDD.'
      }
    }
  }

  if (step === 4) {
    const traits = parsePersonalityTraits(data.personality)
    const allDimensionsSelected = PERSONALITY_DIMENSIONS.every((dimension) => (
      dimension.options.some((option) => traits.includes(option.value))
    ))
    if (!allDimensionsSelected) errors.personality = 'Escolha uma opção em cada grupo de personalidade.'
  }

  return errors
}

function firstIncompleteStep(worker: DigitalWorkerDetail, data: DigitalWorkerCreateFormData): OnboardingWizardStepNumber {
  if (!worker.billing.active) return 2
  if (Object.keys(validateStep(3, data, worker.runtime_capabilities.gateway_channels.supported)).length > 0) return 3
  if (Object.keys(validateStep(4, data, worker.runtime_capabilities.gateway_channels.supported)).length > 0) return 4
  return 7
}

function whatsappReadyForChat(worker: DigitalWorkerDetail): boolean {
  if (!worker.runtime_capabilities.whatsapp_cloud.supported) return true
  if (worker.whatsapp_provider === 'none') return true
  return worker.whatsapp_registration_status === 'verified' || worker.whatsapp_registration_status === 'not_applicable'
}

function integrationCountsAsReady(
  integration: OrgIntegration,
  googleSelectionCount: number,
  metaSelectionCount: number,
): boolean {
  if (integration.status !== 'active') return false
  if (integration.provider === 'google') return googleSelectionCount > 0
  if (integration.provider === 'meta') return metaSelectionCount > 0
  return true
}

function StepActions({
  canGoBack,
  onBack,
  onNext,
  nextLabel,
  nextDisabled = false,
  finalAction,
}: {
  canGoBack?: boolean
  onBack?: () => void
  onNext?: () => void
  nextLabel?: string
  nextDisabled?: boolean
  finalAction?: React.ReactNode
}) {
  return (
    <div className="flex flex-col-reverse gap-3 pt-2 sm:flex-row sm:items-center sm:justify-between">
      <div>{finalAction}</div>
      <div className="flex flex-col gap-2 sm:flex-row">
        {canGoBack && onBack && (
          <Button type="button" variant="outline" onClick={onBack}>
            Voltar
          </Button>
        )}
        {onNext && (
          <Button
            type="button"
            onClick={onNext}
            disabled={nextDisabled}
            className="group transition-all hover:-translate-y-[1px] hover:shadow-lg hover:shadow-violet-500/20"
          >
            {nextLabel ?? 'Continuar'}
            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
          </Button>
        )}
      </div>
    </div>
  )
}


export default function OnboardingLaunchPanel({
  worker,
  integrations,
  availableIntegrations,
  integrationToolkits,
  integrationCategories,
  organizationContext,
  googleAdsAccounts,
  googleAnalyticsProperties,
  metaAdAccounts,
  canManage,
  currentUserName,
  currentUserPhone,
  initialModalToOpen,
  showIdentityHero = true,
  billingPlans,
  canGrantAdminSubscription = false,
  onConfirmStateChange,
}: Props) {
  const defaultResponsibleName = currentUserName?.trim() || ''
  const defaultResponsiblePhone = currentUserPhone ? parseWhatsAppInput(currentUserPhone) : ''
  const initialFormData: DigitalWorkerCreateFormData = {
    name: worker.name,
    job_title: worker.job_title ?? jobTitleForFleetProfile(worker.fleet_profile),
    fleet_profile: worker.fleet_profile,
    contact_name: worker.contact_name?.trim() || defaultResponsibleName,
    contact_whatsapp: worker.contact_whatsapp ? formatWhatsApp(worker.contact_whatsapp) : defaultResponsiblePhone,
    personality: worker.personality ?? '',
    whatsapp_provider: 'none',
    reuse_stationzero_email: false,
    agent_harness: worker.agent_harness,
  }
  const explicitStepInUrl = (() => {
    if (typeof window === 'undefined') return false
    return new URLSearchParams(window.location.search).has('onboarding_step')
  })()
  const requestedInitialStep = stageFromSearchParams(initialModalToOpen)
  const initialAllowedStep = firstIncompleteStep(worker, initialFormData)
  const initialStep = (!explicitStepInUrl && worker.billing.active && requestedInitialStep === 2)
    ? 3
    : Math.min(requestedInitialStep, initialAllowedStep) as OnboardingWizardStepNumber
  const [hasExplicitStepInUrl] = useState(explicitStepInUrl)
  const [activeStep, setActiveStep] = useState<OnboardingWizardStepNumber>(initialStep)
  const [localErrors, setLocalErrors] = useState<FieldErrors>({})
  const [integrationRecommendationOpen, setIntegrationRecommendationOpen] = useState(false)
  const [openIntegrationsSignal, setOpenIntegrationsSignal] = useState(0)
  const avatarStudioRef = useRef<AvatarStudioHandle | null>(null)
  const [savingAvatar, setSavingAvatar] = useState(false)
  const [avatarBusy, setAvatarBusy] = useState(false)
  const [retryingCreation, setRetryingCreation] = useState(false)
  const { celebrate } = useWorkerBornCelebration()

  const form = useForm<DigitalWorkerCreateFormData>(initialFormData)

  const serverErrors = form.errors as FieldErrors
  const mergedErrors = useMemo<FieldErrors>(() => ({ ...serverErrors, ...localErrors }), [serverErrors, localErrors])
  const googleSelectionCount = googleAdsAccounts.filter((account) => account.selected).length +
    googleAnalyticsProperties.filter((property) => property.selected).length
  const metaSelectionCount = metaAdAccounts.filter((account) => account.selected).length
  const activeIntegrationCount = useMemo(
    () => integrations.filter((integration) => integrationCountsAsReady(integration, googleSelectionCount, metaSelectionCount)).length,
    [googleSelectionCount, integrations, metaSelectionCount],
  )
  // WhatsApp injection failures are recoverable via WhatsAppChannelCard's retry button —
  // they should not be treated as a creation failure when the instance itself is healthy.
  const workerCreationFailed = worker.provisioning_status === 'failed' ||
    (worker.whatsapp_registration_status === 'failed' && worker.provisioning_status !== 'ready') ||
    Boolean(worker.provisioning_stale)
  const workerReady = worker.billing.active &&
    worker.provisioning_status === 'ready' &&
    worker.probe_connect_ready &&
    whatsappReadyForChat(worker) &&
    !worker.avatar_generating
  const [chatRevealed, setChatRevealed] = useState(() =>
    initialStep === 7 && workerReady,
  )
  const inConfirmView = activeStep === 7 && !workerCreationFailed && !chatRevealed
  const inChatView = activeStep === 7 && !workerCreationFailed && chatRevealed
  const immersive = inConfirmView || inChatView
  const showWizardExit = inChatView && showIdentityHero
  const showRightAside = showIdentityHero && !immersive

  useEffect(() => {
    replaceOnboardingStepInUrl(activeStep)
  }, [activeStep])

  useEffect(() => {
    const allowedStep = firstIncompleteStep(worker, form.data)
    if (activeStep > allowedStep) setActiveStep(allowedStep)
  }, [activeStep, form.data, worker])

  useEffect(() => {
    if (activeStep < 7) setChatRevealed(false)
  }, [activeStep])

  useEffect(() => {
    onConfirmStateChange?.(immersive)
  }, [immersive, onConfirmStateChange])

  useEffect(() => {
    if (!initialModalToOpen || hasExplicitStepInUrl) return

    const allowedStep = firstIncompleteStep(worker, form.data)
    setActiveStep(Math.min(6, allowedStep) as OnboardingWizardStepNumber)
  }, [form.data, hasExplicitStepInUrl, initialModalToOpen, worker])

  useEffect(() => {
    const fields = Object.keys(serverErrors)
    if (fields.length === 0) return

    const step = stepForField(fields[0])
    if (step >= 2) setActiveStep(step as OnboardingWizardStepNumber)
  }, [serverErrors])

  const advanceTo = useCallback((step: OnboardingWizardStepNumber) => {
    setActiveStep(step)
  }, [])

  const clearErrorsFor = useCallback((fields: DigitalWorkerCreateField[]) => {
    setLocalErrors((previous) => {
      const next = { ...previous }
      fields.forEach((field) => { delete next[field] })
      return next
    })
    form.clearErrors(...fields)
  }, [form])

  const handleFieldChange = useCallback(<K extends DigitalWorkerCreateField>(
    field: K,
    value: DigitalWorkerCreateFormData[K],
  ) => {
    form.setData((previous) => ({ ...previous, [field]: value }))
    clearErrorsFor([field])
  }, [clearErrorsFor, form])

  const persistWorkerUpdate = useCallback((
    payload: Partial<DigitalWorkerCreateFormData>,
    nextStep: OnboardingWizardStepNumber,
  ) => {
    form.transform(() => ({ ...payload, silent: '1' }))
    form.patch(`/dashboard/digital_workers/${worker.id}`, {
      preserveState: true,
      preserveScroll: true,
      onSuccess: () => {
        setLocalErrors({})
        advanceTo(nextStep)
      },
    })
  }, [advanceTo, form, worker.id])

  const handleNext = useCallback(() => {
    if (activeStep === 2) {
      if (worker.billing.active) advanceTo(3)
      return
    }

    if (activeStep === 3) {
      const errors = validateStep(3, form.data, worker.runtime_capabilities.gateway_channels.supported)
      setLocalErrors(errors)
      if (Object.keys(errors).length > 0) return

      const payload = {
        contact_name: form.data.contact_name.trim(),
        ...(worker.runtime_capabilities.gateway_channels.supported
          ? { contact_whatsapp: stripWhatsAppDigits(form.data.contact_whatsapp) }
          : {}),
      }

      if (
        payload.contact_name === (worker.contact_name ?? '') &&
        (!worker.runtime_capabilities.gateway_channels.supported || payload.contact_whatsapp === (worker.contact_whatsapp ?? ''))
      ) {
        advanceTo(4)
      } else {
        persistWorkerUpdate(payload, 4)
      }
      return
    }

    if (activeStep === 4) {
      const errors = validateStep(4, form.data, worker.runtime_capabilities.gateway_channels.supported)
      setLocalErrors(errors)
      if (Object.keys(errors).length > 0) return

      const payload = {
        job_title: form.data.job_title || jobTitleForFleetProfile(form.data.fleet_profile),
        personality: form.data.personality.trim(),
      }

      if (
        payload.job_title === (worker.job_title ?? '') &&
        payload.personality === (worker.personality ?? '')
      ) {
        advanceTo(5)
      } else {
        persistWorkerUpdate(payload, 5)
      }
      return
    }

    if (activeStep === 5) {
      setLocalErrors({})
      const studio = avatarStudioRef.current
      if (studio) {
        setSavingAvatar(true)
        studio.saveCurrent()
          .then((ok) => {
            if (ok) advanceTo(6)
          })
          .finally(() => setSavingAvatar(false))
      } else {
        advanceTo(6)
      }
      return
    }

    if (activeStep === 6) {
      if (activeIntegrationCount < RECOMMENDED_INTEGRATION_COUNT) {
        setIntegrationRecommendationOpen(true)
        return
      }

      advanceTo(7)
    }
  }, [activeIntegrationCount, activeStep, advanceTo, form.data, persistWorkerUpdate, worker])

  const handleBack = () => {
    if (activeStep === 2) return
    setActiveStep((activeStep - 1) as OnboardingWizardStepNumber)
  }

  const handleOpenIntegrationsFromRecommendation = () => {
    setIntegrationRecommendationOpen(false)
    setOpenIntegrationsSignal((value) => value + 1)
  }

  const handleContinueWithFewIntegrations = () => {
    setIntegrationRecommendationOpen(false)
    advanceTo(7)
  }

  const handleRetryCreation = () => {
    if (!canManage || retryingCreation) return

    setRetryingCreation(true)
    router.post(`/dashboard/digital_workers/${worker.id}/retry_provisioning`, {}, {
      preserveScroll: true,
      onFinish: () => setRetryingCreation(false),
    })
  }

  return (
    <section
      data-testid="onboarding-launch-panel"
      className={cn(
        showWizardExit ? 'space-y-3' : 'space-y-5',
        showRightAside && 'lg:grid lg:gap-5 lg:space-y-0 lg:grid-cols-[minmax(0,1fr)_360px] lg:items-start',
      )}
    >
      {showWizardExit && (
        <div className="mx-auto w-full max-w-2xl">
          <Button asChild variant="ghost" size="sm" className="-ml-2">
            <Link href="/dashboard">
              <ArrowLeft className="h-4 w-4" />
              Início
            </Link>
          </Button>
        </div>
      )}

      <div
        className={cn(
          immersive ? 'p-0' : 'rounded-xl border border-border bg-card p-5 shadow-sm sm:p-6',
        )}
        data-testid="onboarding-stage-panel"
        data-step={activeStep}
      >
          <StepTransition stepKey={activeStep}>
            {activeStep === 2 && (
              <SubscribeStep
                worker={worker}
                plans={billingPlans}
                disabled={!canManage}
                canGrantAdminSubscription={canGrantAdminSubscription}
                onContinue={() => advanceTo(3)}
              />
            )}

            {activeStep === 3 && (
              <div className="space-y-5" data-testid="onboarding-responsible-stage">
                <ResponsibleStep
                  showWhatsApp={worker.runtime_capabilities.gateway_channels.supported}
                  data={form.data}
                  errors={mergedErrors}
                  disabled={form.processing || !canManage}
                  onFieldChange={handleFieldChange}
                />

                <StepActions
                  onNext={handleNext}
                  nextDisabled={form.processing || !canManage}
                />
              </div>
            )}

            {activeStep === 4 && (
              <div className="space-y-5" data-testid="onboarding-identity-stage">
                <IdentityStep
                  data={form.data}
                  errors={mergedErrors}
                  disabled={form.processing || !canManage}
                  onFieldChange={handleFieldChange}
                />

                <StepActions
                  canGoBack
                  onBack={handleBack}
                  onNext={handleNext}
                  nextDisabled={form.processing || !canManage}
                />
              </div>
            )}

            {activeStep === 5 && (
              <div className="space-y-5" data-testid="onboarding-avatar-stage">
                <div className="space-y-1.5">
                  <h2 className="text-2xl font-semibold tracking-tight text-foreground">Defina sua aparência</h2>
                  <p className="text-sm text-muted-foreground">
                    Use os seletores para definir a aparência do agente ou gere uma aleatória.
                  </p>
                </div>

                <AvatarStudio
                  ref={avatarStudioRef}
                  worker={worker}
                  canManage={canManage}
                  avatarGenerating={worker.avatar_generating}
                  inlineGenerator
                  hideHeader
                  onBusyChange={setAvatarBusy}
                />

                <StepActions
                  canGoBack
                  onBack={handleBack}
                  onNext={handleNext}
                  nextDisabled={savingAvatar || avatarBusy || !canManage}
                />
              </div>
            )}

            {activeStep === 6 && (
              <div className="space-y-5" data-testid="onboarding-integrations-stage">
                <div className="space-y-1.5">
                  <h2 className="text-2xl font-semibold tracking-tight text-foreground">Conecte suas fontes de dados</h2>
                  <p className="text-sm text-muted-foreground">
                    Escolha pelo menos 2 integrações para a primeira conversa. Você pode adicionar mais depois.
                  </p>
                </div>

                <IntegrationsSection
                  worker={worker}
                  integrations={integrations}
                  availableIntegrations={availableIntegrations}
                  integrationToolkits={integrationToolkits}
                  integrationCategories={integrationCategories}
                  googleAdsAccounts={googleAdsAccounts}
                  googleAnalyticsProperties={googleAnalyticsProperties}
                  metaAdAccounts={metaAdAccounts}
                  canManage={canManage}
                  initialModalToOpen={initialModalToOpen}
                  openConnectModalSignal={openIntegrationsSignal}
                  embedded
                  hideHeader
                />

                <StepActions
                  canGoBack
                  onBack={handleBack}
                  onNext={handleNext}
                />
              </div>
            )}

            {activeStep === 7 && (
              workerCreationFailed ? (
                <div className="space-y-5" data-testid="onboarding-stage-failed" data-step={7}>
                  <div className="rounded-xl border border-destructive/30 bg-destructive/5 p-5">
                    <h2 className="text-xl font-semibold tracking-tight text-foreground">Não conseguimos concluir a criação agora</h2>
                    <p className="mt-2 text-sm text-muted-foreground">
                      Tente novamente agora ou abra o painel completo para continuar depois.
                    </p>
                  </div>

                  <StepActions
                    canGoBack
                    onBack={handleBack}
                    finalAction={(
                      <div className="flex flex-wrap items-center justify-end gap-3">
                        {canManage && (
                          <Button
                            type="button"
                            variant="outline"
                            disabled={retryingCreation}
                            onClick={handleRetryCreation}
                          >
                            {retryingCreation && <Loader2 className="h-4 w-4 animate-spin" />}
                            Tentar novamente
                          </Button>
                        )}
                        <Button asChild variant="ghost" className="px-0 text-muted-foreground hover:text-foreground">
                          <Link href={`/dashboard/digital_workers/${worker.id}`}>Ver painel completo</Link>
                        </Button>
                      </div>
                    )}
                  />
                </div>
              ) : !chatRevealed ? (
                <motion.div
                  key="onboarding-confirm-stage"
                  data-testid="onboarding-confirm-stage"
                  data-step={7}
                  className="flex flex-col items-center gap-6 py-2"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.25 }}
                >
                  <div className="w-full text-center">
                        <h2 className="mt-1 text-2xl font-semibold tracking-tight text-foreground">
                      {workerReady ? 'Esse é o seu colaborador' : 'Estamos dando os toques finais'}
                    </h2>
                    <p className="mt-2 text-sm text-muted-foreground">
                      {workerReady
                        ? 'Confira o resumo. Quando estiver pronto, deslize para começar a conversa.'
                        : 'Seu colaborador ainda está sendo criado. Em instantes a conversa estará liberada.'}
                    </p>
                  </div>

                  <motion.div
                    className="w-full max-w-sm"
                    initial={{ opacity: 0, x: 80, scale: 0.96 }}
                    animate={{ opacity: 1, x: 0, scale: 1 }}
                    transition={{ type: 'spring', stiffness: 220, damping: 28, mass: 0.9 }}
                  >
                    <WorkerProfileCard
                      name={form.data.name || worker.name}
                      fleetProfile={worker.fleet_profile}
                      hueSeed={worker.id}
                      jobTitle={form.data.job_title || worker.job_title}
                      personality={form.data.personality || worker.personality}
                      contactName={form.data.contact_name || worker.contact_name}
                      avatarUrl={worker.avatar_url}
                      revealAvatar
                      integrations={integrations}
                      showProgress={false}
                      status={workerReady ? 'online' : 'creating'}
                    />
                  </motion.div>

                  <AnimatePresence mode="wait">
                    {workerReady ? (
                      <motion.div
                        key="confirm-slide"
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 8 }}
                        transition={{ duration: 0.25 }}
                      >
                        <SlideButton
                          label="Deslize para começar"
                          completedLabel="Vamos lá!"
                          onComplete={() => {
                            try {
                              const audio = new Audio('/sounds/poke-success.mp3')
                              audio.volume = 0.595
                              void audio.play()
                            } catch {
                              // ignore — autoplay policy or unsupported environment
                            }
                            celebrate()
                            window.setTimeout(() => setChatRevealed(true), 1000)
                          }}
                        />
                      </motion.div>
                    ) : (
                      <motion.div
                        key="confirm-waiting"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.25 }}
                        className="flex items-center gap-2 rounded-full border border-border bg-muted/40 px-4 py-2 text-sm text-muted-foreground"
                        data-testid="confirm-waiting-indicator"
                        aria-live="polite"
                      >
                        <span
                          aria-hidden="true"
                          className="loading-spinner-circle-dark animate-loader-circle h-4 w-4 rounded-full"
                        />
                        Finalizando seu colaborador…
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              ) : (
                <div data-testid="onboarding-chat-stage">
                  <OnboardingChat
                    workerId={worker.id}
                    workerName={worker.name}
                    workerRole={worker.job_title}
                    workerProfile={worker.fleet_profile}
                    workerPersonality={worker.personality}
                    contactName={worker.contact_name}
                    contactWhatsApp={worker.contact_whatsapp}
                    workerAvatarUrl={worker.avatar_url}
                    integrations={integrations}
                    organizationContext={organizationContext}
                    googleAdsAccounts={googleAdsAccounts}
                    googleAnalyticsProperties={googleAnalyticsProperties}
                    metaAdAccounts={metaAdAccounts}
                    currentUserName={currentUserName}
                  />
                </div>
              )
            )}
          </StepTransition>
        </div>

        <AnimatePresence>
          {showRightAside && (
            <motion.aside
              key="worker-profile-aside"
              className="lg:sticky lg:top-6 lg:self-start"
              data-testid="worker-profile-aside"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
            >
              <WorkerProfileCard
                name={form.data.name || worker.name}
                fleetProfile={worker.fleet_profile}
                hueSeed={worker.id}
                jobTitle={form.data.job_title || worker.job_title}
                personality={form.data.personality || worker.personality}
                personalityCompleted={activeStep > 4}
                contactName={form.data.contact_name || worker.contact_name}
                avatarUrl={worker.avatar_url}
                avatarCompleted={activeStep > 5}
                revealAvatar={activeStep >= 5}
                integrations={integrations}
              />
            </motion.aside>
          )}
        </AnimatePresence>

      <Dialog open={integrationRecommendationOpen} onOpenChange={setIntegrationRecommendationOpen}>
        <DialogContent data-testid="integration-recommendation-dialog" className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Conecte mais fontes de dados</DialogTitle>
            <DialogDescription>
              Recomendamos conectar pelo menos 2 integrações antes de seguir. Com mais fontes, o colaborador cruza informações e chega ao primeiro chat com insights melhores.
            </DialogDescription>
          </DialogHeader>

          <div className="rounded-lg border border-border bg-muted/30 p-3 text-sm text-muted-foreground">
            Você conectou {activeIntegrationCount} de {RECOMMENDED_INTEGRATION_COUNT} fontes recomendadas.
          </div>

          <DialogFooter className="gap-2 sm:gap-0">
            <Button type="button" variant="ghost" onClick={handleContinueWithFewIntegrations}>
              Continuar mesmo assim
            </Button>
            <Button type="button" onClick={handleOpenIntegrationsFromRecommendation} disabled={!canManage}>
              Conectar fonte de dados
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  )
}
